// SF Galleries — fullscreen lightbox with a single <img> (no nested zoom-dialog).
(() => {
  const OPEN_ATTR = 'data-sfg-open';
  const MODAL_CLASS = 'sf-galleries-modal-open';

  function lockPage() {
    document.documentElement.classList.add(MODAL_CLASS);
    document.documentElement.setAttribute('scroll-lock', '');
  }

  function unlockPage() {
    document.documentElement.classList.remove(MODAL_CLASS);
    document.documentElement.removeAttribute('scroll-lock');
  }

  function wrapIndex(index, count) {
    return ((index % count) + count) % count;
  }

  // Persistent, cross-lightbox cache of preloaded <img> objects so decoded
  // bitmaps stay warm between opens and across sections. Keyed by URL.
  const imgCache = new Map();

  function preload(url) {
    if (!url) return Promise.resolve(false);
    const cached = imgCache.get(url);
    if (cached) {
      if (cached.complete && cached.naturalWidth > 0) return Promise.resolve(true);
      return cached._promise || Promise.resolve(false);
    }
    const img = new Image();
    img.decoding = 'async';
    img._promise = new Promise((resolve) => {
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
    imgCache.set(url, img);
    img.src = url;
    return img._promise;
  }

  function isLoaded(url) {
    const img = imgCache.get(url);
    return !!(img && img.complete && img.naturalWidth > 0);
  }

  function setImgSrc(image, url) {
    if (!url || image.getAttribute('src') === url) return;
    image.src = url;
  }

  class SfLightbox extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      // Top-layer dialogs must not sit inside section stacking contexts.
      // Also remove any stale duplicate with the same id left behind by a
      // theme-editor section re-render, so document.getElementById() always
      // resolves to this live instance (and not an orphaned old one).
      if (this.id) {
        const stale = document.getElementById(this.id);
        if (stale && stale !== this) stale.remove();
      }
      document.body.appendChild(this);

      this._opener = null;
      this._index = 0;
      this._loadToken = 0;
      this._previewsWarmed = false;
      this._images = [];
      this._viewer = this.querySelector('[data-sfg-viewer]');
      this._photo = this.querySelector('.sfg-viewer__photo');

      const dataEl = this.querySelector('[data-sfg-images]');
      if (dataEl?.textContent) {
        try {
          const parsed = JSON.parse(dataEl.textContent);
          if (Array.isArray(parsed)) this._images = parsed;
        } catch {
          /* invalid JSON */
        }
      }

      this.dialog.addEventListener('close', () => this._onClose());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('keydown', (event) => this._onKeydown(event));

      const closeBtn = this.querySelector('[data-sfg-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());

      const stage = this.querySelector('[data-sfg-stage]');

      if (this._viewer?.classList.contains('sfg-viewer--zoomable') && stage) {
        stage.addEventListener('click', (event) => {
          if (event.target.closest('[data-sfg-slide-nav], [data-sfg-dot-index]')) return;
          // A swipe ends with a synthetic click; ignore it so swiping doesn't
          // also toggle zoom.
          if (this._swallowClick) {
            this._swallowClick = false;
            return;
          }
          this._viewer.classList.toggle('sfg-viewer--zoomed');
        });
      }

      if (stage) this._bindSwipe(stage);

      // Nav/dot handlers live on the element itself. A bare stopPropagation here
      // used to kill the document-level click listener, so arrows looked dead
      // while swipe (bound on the stage) still worked.
      this.querySelectorAll('[data-sfg-slide-nav]').forEach((control) => {
        control.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const direction = control.getAttribute('data-sfg-slide-nav') === 'next' ? 1 : -1;
          this.showIndex(this._index + direction);
        });
        control.addEventListener('mousedown', (event) => event.stopPropagation());
      });
      this.querySelectorAll('[data-sfg-dot-index]').forEach((control) => {
        control.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const index = Number.parseInt(control.getAttribute('data-sfg-dot-index') || '', 10);
          if (!Number.isNaN(index)) this.showIndex(index);
        });
        control.addEventListener('mousedown', (event) => event.stopPropagation());
      });
    }

    /**
     * Horizontal touch-swipe to move between images (mobile/tablet). Vertical
     * drags are left alone so the dialog can still be flicked/scrolled, and a
     * swipe suppresses the trailing click so it never toggles zoom.
     * @param {HTMLElement} stage
     */
    _bindSwipe(stage) {
      const THRESHOLD = 40; // px of horizontal travel to count as a swipe
      let startX = 0;
      let startY = 0;
      let tracking = false;
      let horizontal = false;

      const start = (event) => {
        if (event.touches && event.touches.length > 1) return; // ignore pinch
        // Don't start a swipe while zoomed in (tap-to-zoom owns the gesture).
        if (this._viewer?.classList.contains('sfg-viewer--zoomed')) return;
        const point = event.touches ? event.touches[0] : event;
        startX = point.clientX;
        startY = point.clientY;
        tracking = true;
        horizontal = false;
      };

      const move = (event) => {
        if (!tracking) return;
        const point = event.touches ? event.touches[0] : event;
        const dx = point.clientX - startX;
        const dy = point.clientY - startY;
        if (!horizontal && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
          horizontal = true;
        }
        // Once we know it's a horizontal swipe, stop the page/dialog scrolling.
        if (horizontal && event.cancelable) event.preventDefault();
      };

      const end = (event) => {
        if (!tracking) return;
        tracking = false;
        if (this._images.length <= 1) return;
        const point = (event.changedTouches && event.changedTouches[0]) || event;
        const dx = point.clientX - startX;
        const dy = point.clientY - startY;
        if (Math.abs(dx) < THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
        this._swallowClick = true;
        this.showIndex(this._index + (dx < 0 ? 1 : -1));
      };

      stage.addEventListener('touchstart', start, { passive: true });
      stage.addEventListener('touchmove', move, { passive: false });
      stage.addEventListener('touchend', end);
      stage.addEventListener('touchcancel', () => { tracking = false; });
    }

    /**
     * @param {number} index
     * @param {string} [immediate] - a src to paint instantly (e.g. the clicked thumbnail)
     */
    showIndex(index, immediate) {
      if (!this._images.length || !this._photo) return;

      const active = wrapIndex(index, this._images.length);
      const item = this._images[active];
      if (!item?.src) return;

      this._index = active;
      this._viewer?.classList.remove('sfg-viewer--zoomed');
      this._photo.alt = item.alt || '';
      this._photo.classList.remove('is-loading');

      // Guards against out-of-order swaps when the user navigates quickly.
      const token = ++this._loadToken;

      if (isLoaded(item.src)) {
        // Full-resolution image is already warm -> show it instantly, no flash.
        setImgSrc(this._photo, item.src);
      } else {
        // Paint something immediately (the clicked thumbnail, else a light
        // preview) so the viewer never looks blank, then upgrade to full-res.
        setImgSrc(this._photo, immediate || item.preview || item.src);
        preload(item.src).then((ok) => {
          if (ok && token === this._loadToken) setImgSrc(this._photo, item.src);
        });
      }

      this.querySelectorAll('[data-sfg-dot-index]').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === active);
        dot.setAttribute('aria-selected', String(i === active));
      });

      this._preloadAround(active);
    }

    /** Warm immediate neighbours (instant arrow nav) + lazily warm all previews. */
    _preloadAround(active) {
      const n = this._images.length;
      if (n <= 1) return;
      for (const delta of [1, -1]) {
        const it = this._images[wrapIndex(active + delta, n)];
        if (it) preload(it.src);
      }
      if (!this._previewsWarmed) {
        this._previewsWarmed = true;
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
        idle(() => {
          for (const it of this._images) preload(it.preview || it.src);
        });
      }
    }

    /** @param {HTMLElement} [opener] */
    open(opener) {
      if (!this.dialog || this.dialog.open || !this._images.length) return;

      this._opener = opener || null;
      this._viewer?.classList.remove('sfg-viewer--zoomed');

      const startIndex = Number.parseInt(opener?.getAttribute('data-sfg-open-index') || '0', 10);
      // The clicked thumbnail is already decoded on screen; reuse it as an
      // instant placeholder so the lightbox opens with zero perceived wait.
      const thumb = opener?.querySelector('img');
      const immediate = thumb?.currentSrc || thumb?.src || '';

      lockPage();
      this.showIndex(startIndex, immediate);
      this.dialog.showModal();

      const closeBtn = this.querySelector('[data-sfg-close]');
      if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
    }

    close() {
      this._viewer?.classList.remove('sfg-viewer--zoomed');
      if (this.dialog?.open) this.dialog.close();
    }

    _onClose() {
      unlockPage();
      if (this._opener && typeof this._opener.focus === 'function') {
        this._opener.focus();
      }
      this._opener = null;
    }

    /** @param {KeyboardEvent} event */
    _onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showIndex(this._index + 1);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showIndex(this._index - 1);
        return;
      }
      if (event.key === 'Escape' && this._viewer?.classList.contains('sfg-viewer--zoomed')) {
        event.preventDefault();
        this._viewer.classList.remove('sfg-viewer--zoomed');
      }
    }
  }

  if (!customElements.get('sf-lightbox')) {
    customElements.define('sf-lightbox', SfLightbox);
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[' + OPEN_ATTR + ']');
    if (trigger) {
      const id = trigger.getAttribute(OPEN_ATTR);
      if (!id) return;
      const lightbox = document.getElementById(id);
      if (!(lightbox instanceof SfLightbox)) return;
      event.preventDefault();
      event.stopPropagation();
      lightbox.open(trigger);
      return;
    }

    const nav = target.closest('[data-sfg-slide-nav]');
    if (nav) {
      event.preventDefault();
      event.stopPropagation();
      const lightbox = nav.closest('sf-lightbox');
      if (!(lightbox instanceof SfLightbox)) return;
      const direction = nav.getAttribute('data-sfg-slide-nav') === 'next' ? 1 : -1;
      lightbox.showIndex(lightbox._index + direction);
      return;
    }

    const dot = target.closest('[data-sfg-dot-index]');
    if (dot) {
      event.preventDefault();
      event.stopPropagation();
      const lightbox = dot.closest('sf-lightbox');
      if (!(lightbox instanceof SfLightbox)) return;
      const index = Number.parseInt(dot.getAttribute('data-sfg-dot-index') || '', 10);
      if (!Number.isNaN(index)) lightbox.showIndex(index);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[' + OPEN_ATTR + ']');
    if (!trigger) return;
    event.preventDefault();
    const id = trigger.getAttribute(OPEN_ATTR);
    if (!id) return;
    const lightbox = document.getElementById(id);
    if (!(lightbox instanceof SfLightbox)) return;
    lightbox.open(trigger);
  });
})();
